import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";

const links = [
  { label: "Capabilities", id: "capabilities" },
  { label: "Sectors", id: "sectors" },
  { label: "Signature", id: "signature" },
  { label: "Clients", id: "clients" },
  { label: "Contact", id: "contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    if (onHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
    setOpen(false);
  };

  // Scroll to hash on landing if navigated with #id
  useEffect(() => {
    if (onHome && location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        document
          .getElementById(id)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [onHome, location.hash]);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => {
            if (onHome) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
              navigate("/");
            }
          }}
          className="flex items-center group"
        >
          <img
            src="/brand/fina-logo.png"
            alt="Fina Engineering"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-body text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/careers"
            data-testid="nav-link-careers"
            className={`text-sm font-body transition-colors ${
              location.pathname === "/careers"
                ? "text-[#FF5722]"
                : "text-white/70 hover:text-white"
            }`}
          >
            Careers
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-enquire"
            onClick={() => go("contact")}
            className="hidden md:inline-flex items-center h-10 px-5 rounded-full bg-white text-black font-medium text-sm hover:bg-white/90 transition-colors"
          >
            Start a project
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-10 h-10 grid place-items-center rounded-full border border-white/15 text-white"
            aria-label="Menu"
          >
            <span className="block w-4 h-[1.5px] bg-white relative before:content-[''] before:absolute before:-top-1.5 before:left-0 before:w-4 before:h-[1.5px] before:bg-white after:content-[''] after:absolute after:top-1.5 after:left-0 after:w-4 after:h-[1.5px] after:bg-white" />
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden border-t border-white/10 bg-black/80 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left text-white/80 font-heading text-2xl"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/careers"
              data-testid="nav-mobile-link-careers"
              onClick={() => setOpen(false)}
              className="text-left text-white/80 font-heading text-2xl"
            >
              Careers
            </Link>
            <button
              data-testid="nav-mobile-cta"
              onClick={() => go("contact")}
              className="mt-2 inline-flex items-center justify-center h-11 rounded-full bg-[#FF5722] text-black font-medium"
            >
              Start a project
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
};
